import api from '@/lib/api';
import axios from 'axios';
import type {
  AsistenteRecipeResult,
  CobradorRecipeResult,
  ConserjeRecipeResult,
  CreateAsistenteRequest,
  CreateCobradorRequest,
  CreateConserjeRequest,
  CreateMesaRequest,
  CreateVendedorRequest,
  MesaRecipeResult,
  RecipeDestinationOption,
  VendedorRecipeResult,
} from '@workspace/types';
import type {
  CreatePlatformTenantRequest,
  CreatePlatformUserRequest,
  PlatformUserBotAccess,
  PlatformUserFeatureAccess,
  PlatformTenant,
  PlatformUser,
  UpdatePlatformUserBotAccessRequest,
  UpdatePlatformUserFeatureAccessRequest,
  UpdatePlatformTenantRequest,
  UpdatePlatformUserRequest,
} from '../types/platformAdmin.types';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
};

function unwrap<T>(response: { data: ApiResponse<T> }, fallback: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || response.data.message || fallback);
  }
  return response.data.data;
}

type ValidationIssue = {
  path?: Array<string | number>;
  message?: string;
};

function describeValidationIssue(issue: ValidationIssue): string {
  const field = issue.path?.join('.');
  if (field === 'email') return 'El email ingresado no tiene un formato válido.';
  if (field === 'password') return 'La contraseña debe tener entre 8 y 128 caracteres.';
  if (field === 'tenantId') return 'Seleccioná un tenant válido para el usuario.';
  if (field === 'role') return 'Seleccioná un rol válido para el usuario.';
  if (field === 'status') return 'Seleccioná un estado válido para el usuario.';
  if (field === 'telegramId') return 'El ID de Telegram debe ser numérico.';
  if (field === 'whatsappPhone') return 'El celular de WhatsApp no tiene un formato válido.';
  return issue.message || 'Revisá los datos ingresados.';
}

function formatBackendErrorMessage(message: string, fallback: string): string {
  if (message.includes('Unique constraint') && message.includes('email')) {
    return 'Ya existe un usuario con ese email. Usá otro email o editá el usuario existente.';
  }

  if (message.includes('Tenant requerido')) {
    return 'Seleccioná un tenant para el usuario. Los usuarios ADMIN y USER siempre deben pertenecer a un tenant.';
  }

  try {
    const issues = JSON.parse(message) as ValidationIssue[];
    if (Array.isArray(issues) && issues.length > 0) {
      return describeValidationIssue(issues[0]);
    }
  } catch {
    // The backend may also return plain, already-readable messages.
  }

  return message || fallback;
}

export function getPlatformAdminErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError<ApiResponse<unknown>>(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const responseMessage = error.response?.data?.error || error.response?.data?.message;
  if (responseMessage) {
    return formatBackendErrorMessage(responseMessage, fallback);
  }

  const status = error.response?.status;
  if (status === 400) return `${fallback}: revisá que los datos ingresados sean válidos.`;
  if (status === 401) return `${fallback}: tu sesión expiró o no estás autenticado.`;
  if (status === 403) return `${fallback}: no tenés permisos para realizar esta acción.`;
  if (status === 409) return `${fallback}: ya existe un registro con esos datos.`;

  return status ? `${fallback}: el servidor respondió con estado ${status}.` : fallback;
}

async function runRecipe<T>(recipe: string, data: unknown, fallback: string): Promise<T> {
  try {
    const response = await api.post<ApiResponse<T>>(`/admin/platform/recipes/${recipe}`, data);
    return unwrap(response, fallback);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = (error.response?.data as ApiResponse<unknown> | undefined)?.error;
      if (typeof message === 'string' && message) {
        throw new Error(message);
      }
    }
    throw error;
  }
}

export const platformAdminApi = {
  async listTenants(): Promise<PlatformTenant[]> {
    const response = await api.get<ApiResponse<PlatformTenant[]>>('/admin/platform/tenants');
    return unwrap(response, 'Error al listar tenants');
  },

  async getTenant(id: string): Promise<PlatformTenant> {
    const response = await api.get<ApiResponse<PlatformTenant>>(`/admin/platform/tenants/${id}`);
    return unwrap(response, 'Error al obtener tenant');
  },

  async createTenant(data: CreatePlatformTenantRequest): Promise<PlatformTenant> {
    const response = await api.post<ApiResponse<PlatformTenant>>('/admin/platform/tenants', data);
    return unwrap(response, 'Error al crear tenant');
  },

  async updateTenant(id: string, data: UpdatePlatformTenantRequest): Promise<PlatformTenant> {
    const response = await api.put<ApiResponse<PlatformTenant>>(
      `/admin/platform/tenants/${id}`,
      data
    );
    return unwrap(response, 'Error al actualizar tenant');
  },

  async deleteTenant(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/admin/platform/tenants/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al desactivar tenant');
    }
  },

  async listUsers(tenantId?: string): Promise<PlatformUser[]> {
    const response = await api.get<ApiResponse<PlatformUser[]>>('/admin/platform/users', {
      params: tenantId ? { tenantId } : {},
    });
    return unwrap(response, 'Error al listar usuarios');
  },

  async getUser(id: number): Promise<PlatformUser> {
    const response = await api.get<ApiResponse<PlatformUser>>(`/admin/platform/users/${id}`);
    return unwrap(response, 'Error al obtener usuario');
  },

  async createUser(data: CreatePlatformUserRequest): Promise<PlatformUser> {
    const response = await api.post<ApiResponse<PlatformUser>>('/admin/platform/users', data);
    return unwrap(response, 'Error al crear usuario');
  },

  async updateUser(id: number, data: UpdatePlatformUserRequest): Promise<PlatformUser> {
    const response = await api.put<ApiResponse<PlatformUser>>(`/admin/platform/users/${id}`, data);
    return unwrap(response, 'Error al actualizar usuario');
  },

  async deleteUser(id: number): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/admin/platform/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al desactivar usuario');
    }
  },

  async resetUserPassword(id: number, password: string): Promise<void> {
    const response = await api.post<ApiResponse<void>>(
      `/admin/platform/users/${id}/reset-password`,
      { password }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Error al cambiar contraseña');
    }
  },

  async getUserBotAccess(id: number): Promise<PlatformUserBotAccess> {
    const response = await api.get<ApiResponse<PlatformUserBotAccess>>(
      `/admin/platform/users/${id}/bot-access`
    );
    return unwrap(response, 'Error al obtener permisos de bots');
  },

  async updateUserBotAccess(
    id: number,
    data: UpdatePlatformUserBotAccessRequest
  ): Promise<PlatformUserBotAccess> {
    const response = await api.put<ApiResponse<PlatformUserBotAccess>>(
      `/admin/platform/users/${id}/bot-access`,
      data
    );
    return unwrap(response, 'Error al actualizar permisos de bots');
  },

  async getUserFeatureAccess(id: number): Promise<PlatformUserFeatureAccess> {
    const response = await api.get<ApiResponse<PlatformUserFeatureAccess>>(
      `/admin/platform/users/${id}/feature-access`
    );
    return unwrap(response, 'Error al obtener permisos de módulos');
  },

  async updateUserFeatureAccess(
    id: number,
    data: UpdatePlatformUserFeatureAccessRequest
  ): Promise<PlatformUserFeatureAccess> {
    const response = await api.put<ApiResponse<PlatformUserFeatureAccess>>(
      `/admin/platform/users/${id}/feature-access`,
      data
    );
    return unwrap(response, 'Error al actualizar permisos de módulos');
  },

  async createCobrador(data: CreateCobradorRequest): Promise<CobradorRecipeResult> {
    return runRecipe<CobradorRecipeResult>('cobrador', data, 'No se pudo crear el Cobrador');
  },

  async createVendedor(data: CreateVendedorRequest): Promise<VendedorRecipeResult> {
    return runRecipe<VendedorRecipeResult>('vendedor', data, 'No se pudo crear el Vendedor');
  },

  async createMesa(data: CreateMesaRequest): Promise<MesaRecipeResult> {
    return runRecipe<MesaRecipeResult>('mesa', data, 'No se pudo crear la Mesa de ayuda');
  },

  async createAsistente(data: CreateAsistenteRequest): Promise<AsistenteRecipeResult> {
    return runRecipe<AsistenteRecipeResult>('asistente', data, 'No se pudo crear el Asistente');
  },

  async createConserje(data: CreateConserjeRequest): Promise<ConserjeRecipeResult> {
    return runRecipe<ConserjeRecipeResult>('conserje', data, 'No se pudo crear el Conserje');
  },

  async listRecipeDestinations(tenantId: string): Promise<RecipeDestinationOption[]> {
    const response = await api.get<ApiResponse<RecipeDestinationOption[]>>('/admin/platform/recipes/destinations', {
      params: { tenantId },
    });
    return unwrap(response, 'No se pudieron listar los agentes del cliente');
  },
};
